import { aptGenerationApi } from '@/api/apt_generation.api';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { TextViewDialog } from '@/components/dialogs/TextViewDialog';
import { ToolpathDialog } from '@/components/dialogs/ToolpathDialog';
import { useDetections } from '@/hooks/useDetections';
import type { DrillSide } from '@/types/detection.types';
import { ORIENTATION_ORDER } from '@/types/detection.types';
import type { HoleObjectModel } from '@/types/apt_generation.types';
import type { ProcedureModel } from '@/types/procedure.types';
import type { PocketObjectModel, PocketProcedureModel } from '@/types/pocket.types';
import { Activity, AlertTriangle, Box, ChevronDown, ChevronRight, Download, FileText } from 'lucide-react';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { type FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface PocketsPanelProps {
  projectId: string;
  targetModelId?: string | null;
}

function sortDetectionsByOrientation<T extends { orientation: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ia = ORIENTATION_ORDER.indexOf(a.orientation as DrillSide);
    const ib = ORIENTATION_ORDER.indexOf(b.orientation as DrillSide);
    if (ia === -1 && ib === -1) return a.orientation.localeCompare(b.orientation);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

interface TextDialogState {
  title: string;
  content: string;
  filename: string;
}

export const PocketsPanel: FC<PocketsPanelProps> = ({ projectId, targetModelId }) => {
  const { t } = useTranslation();
  const { detections, isLoading, error: detectionsError } = useDetections(projectId);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [pocketsData, setPocketsData] = useState<Record<string, PocketObjectModel[]>>({});
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [loadingExtractionId, setLoadingExtractionId] = useState<string | null>(null);
  const pocketsDataRef = useRef(pocketsData);
  pocketsDataRef.current = pocketsData;

  const [textDialog, setTextDialog] = useState<TextDialogState | null>(null);
  const [loadingFileKey, setLoadingFileKey] = useState<string | null>(null);
  const [expandedHFiles, setExpandedHFiles] = useState<Set<string>>(new Set());
  const [isDownloadingPocketsZip, setIsDownloadingPocketsZip] = useState(false);
  const [downloadingZipProcId, setDownloadingZipProcId] = useState<string | null>(null);
  const [toolpathDialog, setToolpathDialog] = useState<
    { procedureId: string; title: string } | null
  >(null);

  const [chamfersData, setChamfersData] = useState<Record<string, HoleObjectModel[]>>({});

  const pocketExtractions = useMemo(() => {
    if (!targetModelId) return [];
    return sortDetectionsByOrientation(
      detections.filter((d) => d.extraction_type === 'pockets' && d.model_id === targetModelId),
    );
  }, [detections, targetModelId]);

  const holeExtractions = useMemo(() => {
    if (!targetModelId) return [];
    return sortDetectionsByOrientation(
      detections.filter((d) => d.extraction_type === 'holes' && d.model_id === targetModelId),
    );
  }, [detections, targetModelId]);

  useEffect(() => {
    if (activeTabIndex >= pocketExtractions.length && pocketExtractions.length > 0) {
      setActiveTabIndex(0);
    }
  }, [activeTabIndex, pocketExtractions.length]);

  useEffect(() => {
    const ext = pocketExtractions[activeTabIndex];
    if (!ext) return;
    if (pocketsDataRef.current[ext.id] !== undefined) return;

    let cancelled = false;
    setFetchError(null);
    setLoadingExtractionId(ext.id);

    void aptGenerationApi
      .getPocketsByExtractionId(ext.id)
      .then((rows) => {
        if (!cancelled) {
          setPocketsData((prev) => ({ ...prev, [ext.id]: rows }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetchError(t('projectDetail.pocketsFetchError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingExtractionId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTabIndex, pocketExtractions, t]);

  // Fetch chamfers for the holes extraction matching the active tab orientation
  useEffect(() => {
    const pocketExt = pocketExtractions[activeTabIndex];
    if (!pocketExt) return;
    const holeExt = holeExtractions.find((h) => h.orientation === pocketExt.orientation);
    if (!holeExt) return;
    if (chamfersData[holeExt.id] !== undefined) return;

    let cancelled = false;
    void aptGenerationApi
      .getChamfersByExtractionId(holeExt.id)
      .then((rows) => {
        if (!cancelled) setChamfersData((prev) => ({ ...prev, [holeExt.id]: rows }));
      })
      .catch(() => { /* chamfers are optional — silently ignore */ });

    return () => { cancelled = true; };
  }, [activeTabIndex, pocketExtractions, holeExtractions, chamfersData]);

  const handleViewAptSource = async (proc: PocketProcedureModel) => {
    const key = `apt-${proc.id}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getPocketProcedureAptSource(proc.id);
      const text = await blob.text();
      const filename = `pocket_procedure_${proc.id}.aptsource`;
      setTextDialog({
        title: `${t('projectDetail.pocketsAptSourceDialogTitle')} — pass ${proc.pass_index}`,
        content: text,
        filename,
      });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const handleViewHFile = async (proc: PocketProcedureModel, filename: string) => {
    const key = `h-${proc.id}-${filename}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getPocketProcedureHFile(proc.id, filename);
      const text = await blob.text();
      setTextDialog({
        title: `${t('projectDetail.pocketsHFileDialogTitle')} — ${filename}`,
        content: text,
        filename,
      });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const toggleHFiles = (procId: string) => {
    setExpandedHFiles((prev) => {
      const next = new Set(prev);
      if (next.has(procId)) {
        next.delete(procId);
      } else {
        next.add(procId);
      }
      return next;
    });
  };

  const handleDownloadProcedureHFilesZip = async (procId: string) => {
    setDownloadingZipProcId(procId);
    try {
      const response = await aptGenerationApi.downloadPocketProcedureHFilesZip(procId);
      let filename = `h-files_${procId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Download failed');
    } finally {
      setDownloadingZipProcId(null);
    }
  };

  const handleDownloadPocketsZip = async () => {
    setIsDownloadingPocketsZip(true);
    try {
      const response = await aptGenerationApi.downloadProjectPocketsHFilesZip(projectId);
      let filename = `h_files_pockets_project_${projectId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Download failed');
    } finally {
      setIsDownloadingPocketsZip(false);
    }
  };

  const handleViewChamferAptSource = async (proc: ProcedureModel) => {
    const key = `apt-${proc.id}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.downloadProcedureAPTSource(proc.id);
      const text = await blob.text();
      setTextDialog({ title: `${t('projectDetail.pocketsAptSourceDialogTitle')} — ${proc.operation_name}`, content: text, filename: `chamfer_${proc.id}.aptsource` });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const handleViewChamferHFile = async (proc: ProcedureModel, filename: string) => {
    const key = `h-${proc.id}-${filename}`;
    setLoadingFileKey(key);
    try {
      const blob = await aptGenerationApi.getProcedureHFile(proc.id, filename);
      const text = await blob.text();
      setTextDialog({ title: `${t('projectDetail.pocketsHFileDialogTitle')} — ${filename}`, content: text, filename });
    } catch {
      toast.error(t('projectDetail.pocketsFetchError'));
    } finally {
      setLoadingFileKey(null);
    }
  };

  const handleDownloadChamferHFilesZip = async (procId: string) => {
    setDownloadingZipProcId(procId);
    try {
      const response = await aptGenerationApi.downloadProcedureHFilesZip(procId);
      let filename = `h-files_${procId}.zip`;
      const contentDisposition = (response.headers as Record<string, string>)?.['content-disposition'];
      if (contentDisposition) {
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        if (match?.[1]) filename = match[1];
      }
      const url = URL.createObjectURL(new Blob([response.data as BlobPart]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('projectDetail.downloadError') || 'Download failed');
    } finally {
      setDownloadingZipProcId(null);
    }
  };

  if (!targetModelId) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-3 mb-4">
          <Box size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.pocketsTitle')}</h2>
        </div>
        <EmptyState title={t('projectDetail.pocketsTitle')} message={t('projectDetail.pocketsNoTargetModel')} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-3 mb-6">
          <Box size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.pocketsTitle')}</h2>
        </div>
        <div className="flex justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (detectionsError) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-3 mb-4">
          <Box size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.pocketsTitle')}</h2>
        </div>
        <ErrorMessage message={detectionsError} />
      </div>
    );
  }

  if (pocketExtractions.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center gap-3 mb-4">
          <Box size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.pocketsTitle')}</h2>
        </div>
        <EmptyState title={t('projectDetail.pocketsTitle')} message={t('projectDetail.pocketsNoExtractions')} />
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Box size={24} className="text-primary shrink-0" />
            <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.pocketsTitle')}</h2>
          </div>
          <Button
            type="button"
            label={t('projectDetail.downloadProjectPocketsHFiles')}
            icon={<Download size={16} className="mr-2" />}
            onClick={() => void handleDownloadPocketsZip()}
            loading={isDownloadingPocketsZip}
            disabled={isDownloadingPocketsZip}
            outlined
            size="small"
            className="shrink-0"
            tooltip={t('projectDetail.downloadProjectPocketsHFilesTooltip')}
            tooltipOptions={{ position: 'bottom' }}
            aria-label={t('projectDetail.downloadProjectPocketsHFilesTooltip')}
          />
        </div>

        {fetchError ? <ErrorMessage message={fetchError} /> : null}

        <div className="rounded-lg border border-gray-200 overflow-hidden [&_.p-tabview-panels]:p-4 [&_.p-tabview-panel]:p-0 [&_.p-tabview-nav]:mb-0 [&_.p-tabview-nav]:border-0 [&_.p-tabview-nav-container]:border-b [&_.p-tabview-nav-container]:border-gray-200">
          <TabView
            activeIndex={activeTabIndex}
            onTabChange={(e) => setActiveTabIndex(e.index)}
            className="[&_.p-tablist]:flex [&_.p-tablist]:gap-1 [&_.p-tablist]:flex-wrap"
          >
            {pocketExtractions.map((extraction) => {
              const pockets = pocketsData[extraction.id];
              const count = pockets !== undefined ? pockets.length : '…';
              const sortedPockets =
                pockets !== undefined ? [...pockets].sort((a, b) => a.order_number - b.order_number) : [];

              const holeExt = holeExtractions.find((h) => h.orientation === extraction.orientation);
              const chamferHoles = holeExt ? (chamfersData[holeExt.id] ?? null) : null;
              // Group chamfer holes by their shared procedure (same procedure shared across all holes in a group)
              const chamferGroups: Map<string, { proc: ProcedureModel; holes: HoleObjectModel[] }> = new Map();
              if (chamferHoles) {
                for (const hole of chamferHoles) {
                  for (const proc of hole.procedures) {
                    if (!chamferGroups.has(proc.id)) {
                      chamferGroups.set(proc.id, { proc, holes: [] });
                    }
                    chamferGroups.get(proc.id)!.holes.push(hole);
                  }
                }
              }
              const chamferGroupList = [...chamferGroups.values()].sort((a, b) =>
                a.proc.order_number - b.proc.order_number
              );

              return (
                <TabPanel
                  key={extraction.id}
                  header={
                    <div className="flex items-center gap-2">
                      <span>{extraction.orientation}</span>
                      <span className="text-xs text-gray-500">({count})</span>
                    </div>
                  }
                >
                  {loadingExtractionId === extraction.id && pockets === undefined ? (
                    <div className="flex justify-center py-10">
                      <LoadingSpinner size="medium" />
                    </div>
                  ) : pockets !== undefined && sortedPockets.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('projectDetail.pocketsEmptyOrientation')}</p>
                  ) : pockets !== undefined ? (
                    <div className="space-y-8">
                      {sortedPockets.map((pocket) => (
                        <div key={pocket.id} className="border border-gray-100 rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="text-left py-2 px-3 font-medium text-gray-700">
                                  {t('projectDetail.pocketsFeatureId')}
                                </th>
                                <th className="text-left py-2 px-3 font-medium text-gray-700">
                                  {t('projectDetail.pocketsKind')}
                                </th>
                                <th className="text-left py-2 px-3 font-medium text-gray-700">
                                  {t('projectDetail.pocketsOrder')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-gray-100">
                                <td className="py-2 px-3 font-mono text-xs">{pocket.feature_id}</td>
                                <td className="py-2 px-3">{pocket.kind}</td>
                                <td className="py-2 px-3 tabular-nums">{pocket.order_number}</td>
                              </tr>
                            </tbody>
                          </table>

                          <div className="px-3 py-3 bg-gray-50/80 border-t border-gray-100">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                              {t('projectDetail.pocketsProceduresHeading')}
                            </p>
                            {pocket.pocket_procedures.length === 0 ? (
                              <div className="flex items-start gap-2 text-sm text-red-600">
                                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <span>{t('projectDetail.pocketsAptFailed')}</span>
                                  {pocket.apt_error && (
                                    <p className="text-xs text-red-500 mt-0.5 break-all">{pocket.apt_error}</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                    <th className="py-1.5 pr-3">{t('projectDetail.pocketsPass')}</th>
                                    <th className="py-1.5 pr-3">{t('projectDetail.pocketsOperation')}</th>
                                    <th className="py-1.5 pr-3">{t('projectDetail.toolNumber')}</th>
                                    <th className="py-1.5 pr-3">{t('toolManagement.toolName')}</th>
                                    <th className="py-1.5 pr-3">{t('projectDetail.spindleSpeed')}</th>
                                    <th className="py-1.5 pr-3">{t('toolManagement.feedMachValue')}</th>
                                    <th className="py-1.5" aria-hidden />
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...pocket.pocket_procedures]
                                    .sort((a, b) => a.pass_index - b.pass_index)
                                    .map((proc) => {
                                      const hasApt = Boolean(proc.apt_s3_url);
                                      const hFilesList = proc.h_files ?? [];
                                      const hasPplog = Boolean(proc.h_files_pplog);
                                      const hasHFiles = hFilesList.length > 0 || hasPplog;
                                      const hFilesExpanded = expandedHFiles.has(proc.id);

                                      return (
                                        <React.Fragment key={proc.id}>
                                          <tr className={`border-b border-gray-100 last:border-0 ${proc.error ? 'bg-red-50' : ''}`}>
                                            <td className="py-2 pr-3">
                                              <div className="flex items-center gap-1 min-w-0">
                                                {hasHFiles ? (
                                                  <button
                                                    type="button"
                                                    onClick={() => toggleHFiles(proc.id)}
                                                    className={`p-0.5 rounded shrink-0 ${
                                                      hFilesExpanded
                                                        ? 'text-blue-700'
                                                        : 'text-gray-500 hover:text-blue-600'
                                                    }`}
                                                    aria-expanded={hFilesExpanded}
                                                    title={t('projectDetail.pocketsViewHFiles')}
                                                  >
                                                    {hFilesExpanded ? (
                                                      <ChevronDown size={16} aria-hidden />
                                                    ) : (
                                                      <ChevronRight size={16} aria-hidden />
                                                    )}
                                                  </button>
                                                ) : (
                                                  <span className="w-[22px] shrink-0" aria-hidden />
                                                )}
                                                <span className="tabular-nums">{proc.pass_index}</span>
                                              </div>
                                            </td>
                                            <td className="py-2 pr-3">{proc.operation_name}</td>
                                            <td className="py-2 pr-3 tabular-nums">
                                              {proc.tool_number != null ? proc.tool_number : '—'}
                                            </td>
                                            <td className="py-2 pr-3">{proc.tool_name ?? '—'}</td>
                                            <td className="py-2 pr-3 tabular-nums">{proc.spindle_speed_rpm}</td>
                                            <td className="py-2 pr-3 tabular-nums">{proc.feed_cut}</td>
                                            <td className="py-2">
                                              <div className="flex items-center gap-1">
                                                {hasApt && (
                                                  <button
                                                    type="button"
                                                    disabled={loadingFileKey === `apt-${proc.id}`}
                                                    onClick={() => void handleViewAptSource(proc)}
                                                    className="flex items-center gap-1 px-1.5 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                                    title={t('projectDetail.pocketsViewAptSource')}
                                                  >
                                                    <FileText size={13} />
                                                    <span>APT</span>
                                                  </button>
                                                )}
                                                {proc.toolpath_html_s3_url && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      setToolpathDialog({
                                                        procedureId: proc.id,
                                                        title: `${t('projectDetail.toolpathDialogTitle')} — ${pocket.feature_id} · pass ${proc.pass_index}`,
                                                      })
                                                    }
                                                    className="flex items-center gap-1 px-1.5 py-1 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 rounded transition-colors"
                                                    title={t('projectDetail.toolpathButtonTooltip')}
                                                  >
                                                    <Activity size={13} />
                                                    <span>{t('projectDetail.toolpathButtonLabel')}</span>
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                          {proc.error && (
                                            <tr className="border-b border-gray-100 bg-red-50">
                                              <td colSpan={7} className="py-1.5 px-3 pl-9">
                                                <div className="flex items-start gap-2 text-xs text-red-700">
                                                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                                  <span className="break-all">{proc.error}</span>
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                          {hFilesExpanded && (
                                            <>
                                              <tr className="border-b border-gray-200 bg-blue-50/70 text-gray-800">
                                                <td colSpan={7} className="py-1.5 px-3 align-middle">
                                                  <div className="flex items-center gap-2 min-w-0 w-full pl-6">
                                                    <span className="font-medium text-gray-600 shrink-0 text-xs">
                                                      {t('projectDetail.hFilesSectionLabel')}
                                                    </span>
                                                    <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
                                                      {hasPplog && proc.h_files_pplog && (
                                                        <button
                                                          type="button"
                                                          disabled={loadingFileKey === `h-${proc.id}-${proc.h_files_pplog}`}
                                                          onClick={() => void handleViewHFile(proc, proc.h_files_pplog!)}
                                                          className="flex items-center gap-1.5 px-2 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors shrink-0 text-xs min-w-0 disabled:opacity-50"
                                                          title={t('projectDetail.viewPplogFile')}
                                                          aria-label={t('projectDetail.viewPplogFile')}
                                                        >
                                                          <FileText size={13} className="shrink-0" />
                                                          <span className="break-all hyphens-auto min-w-0 truncate">
                                                            {proc.h_files_pplog}
                                                          </span>
                                                        </button>
                                                      )}
                                                      <button
                                                        type="button"
                                                        disabled={downloadingZipProcId === proc.id}
                                                        onClick={() => void handleDownloadProcedureHFilesZip(proc.id)}
                                                        className="flex items-center gap-1.5 px-2 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors shrink-0 text-xs disabled:opacity-50"
                                                        title={t('projectDetail.downloadHFilesZipTooltip')}
                                                        aria-label={t('projectDetail.downloadHFilesZipTooltip')}
                                                      >
                                                        <Download size={13} />
                                                        <span>{t('projectDetail.downloadHFilesZipButton')}</span>
                                                      </button>
                                                    </div>
                                                  </div>
                                                </td>
                                              </tr>
                                              {hFilesList.map((filename) => (
                                                <tr key={filename} className="border-b border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                                                  <td colSpan={7} className="py-1.5 px-3 align-middle text-gray-700">
                                                    <div className="flex items-center gap-2 min-w-0 pl-6">
                                                      <span className="break-all hyphens-auto min-w-0 text-xs">{filename}</span>
                                                      <button
                                                        type="button"
                                                        disabled={loadingFileKey === `h-${proc.id}-${filename}`}
                                                        onClick={() => void handleViewHFile(proc, filename)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors shrink-0 disabled:opacity-50"
                                                        title={t('projectDetail.viewHFile')}
                                                        aria-label={t('projectDetail.viewHFile')}
                                                      >
                                                        <FileText size={14} />
                                                      </button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              ))}
                                            </>
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex justify-center py-10">
                      <LoadingSpinner size="medium" />
                    </div>
                  )}

                  {/* ── Chamfers section ── */}
                  {chamferGroupList.length > 0 && (
                    <div className="mt-6">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                        {t('projectDetail.chamfersHeading')}
                      </p>
                      <div className="space-y-4">
                        {chamferGroupList.map(({ proc, holes }) => {
                          const hasApt = Boolean(proc.apt_s3_url);
                          const hFilesList = proc.h_files ?? [];
                          const hasPplog = Boolean(proc.h_files_pplog);
                          const hasHFiles = hFilesList.length > 0 || hasPplog;
                          const hFilesExpanded = expandedHFiles.has(proc.id);
                          return (
                            <div key={proc.id} className="border border-gray-100 rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr className="text-left text-xs text-gray-500">
                                    <th className="py-1.5 px-3">{t('projectDetail.pocketsOperation')}</th>
                                    <th className="py-1.5 px-3">{t('toolManagement.toolName')}</th>
                                    <th className="py-1.5 px-3">{t('projectDetail.spindleSpeed')}</th>
                                    <th className="py-1.5 px-3">{t('toolManagement.feedMachValue')}</th>
                                    <th className="py-1.5 px-3">{t('projectDetail.chamfersHolesCount')}</th>
                                    <th className="py-1.5" aria-hidden />
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-b border-gray-100">
                                    <td className="py-2 px-3 text-xs font-mono">{proc.operation_name}</td>
                                    <td className="py-2 px-3">{proc.assembly.tool?.tool_name ?? '—'}</td>
                                    <td className="py-2 px-3 tabular-nums">{proc.spindle_speed_rpm}</td>
                                    <td className="py-2 px-3 tabular-nums">{proc.feed_cut}</td>
                                    <td className="py-2 px-3 tabular-nums">{holes.length}</td>
                                    <td className="py-2 px-3">
                                      <div className="flex items-center gap-1">
                                        {hasHFiles && (
                                          <button
                                            type="button"
                                            onClick={() => toggleHFiles(proc.id)}
                                            className={`p-0.5 rounded shrink-0 ${hFilesExpanded ? 'text-blue-700' : 'text-gray-500 hover:text-blue-600'}`}
                                            aria-expanded={hFilesExpanded}
                                            title={t('projectDetail.pocketsViewHFiles')}
                                          >
                                            {hFilesExpanded ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                                          </button>
                                        )}
                                        {hasApt && (
                                          <button
                                            type="button"
                                            disabled={loadingFileKey === `apt-${proc.id}`}
                                            onClick={() => void handleViewChamferAptSource(proc)}
                                            className="flex items-center gap-1 px-1.5 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                            title={t('projectDetail.pocketsViewAptSource')}
                                          >
                                            <FileText size={13} />
                                            <span>APT</span>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                  {hFilesExpanded && (
                                    <>
                                      <tr className="border-b border-gray-200 bg-blue-50/70">
                                        <td colSpan={6} className="py-1.5 px-3">
                                          <div className="flex items-center gap-2 w-full pl-4">
                                            <span className="font-medium text-gray-600 shrink-0 text-xs">{t('projectDetail.hFilesSectionLabel')}</span>
                                            <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
                                              {hasPplog && proc.h_files_pplog && (
                                                <button
                                                  type="button"
                                                  disabled={loadingFileKey === `h-${proc.id}-${proc.h_files_pplog}`}
                                                  onClick={() => void handleViewChamferHFile(proc, proc.h_files_pplog!)}
                                                  className="flex items-center gap-1.5 px-2 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded text-xs disabled:opacity-50"
                                                  title={t('projectDetail.viewPplogFile')}
                                                >
                                                  <FileText size={13} />
                                                  <span className="truncate">{proc.h_files_pplog}</span>
                                                </button>
                                              )}
                                              <button
                                                type="button"
                                                disabled={downloadingZipProcId === proc.id}
                                                onClick={() => void handleDownloadChamferHFilesZip(proc.id)}
                                                className="flex items-center gap-1.5 px-2 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded text-xs disabled:opacity-50"
                                                title={t('projectDetail.downloadHFilesZipTooltip')}
                                              >
                                                <Download size={13} />
                                                <span>{t('projectDetail.downloadHFilesZipButton')}</span>
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                      {hFilesList.map((filename: string) => (
                                        <tr key={filename} className="border-b border-gray-100 bg-gray-50/50 hover:bg-gray-50">
                                          <td colSpan={6} className="py-1.5 px-3">
                                            <div className="flex items-center gap-2 pl-4">
                                              <span className="break-all text-xs">{filename}</span>
                                              <button
                                                type="button"
                                                disabled={loadingFileKey === `h-${proc.id}-${filename}`}
                                                onClick={() => void handleViewChamferHFile(proc, filename)}
                                                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded shrink-0 disabled:opacity-50"
                                                title={t('projectDetail.viewHFile')}
                                              >
                                                <FileText size={14} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </TabPanel>
              );
            })}
          </TabView>
        </div>
      </div>

      {textDialog && (
        <TextViewDialog
          visible={true}
          title={textDialog.title}
          textContent={textDialog.content}
          downloadFilename={textDialog.filename}
          onClose={() => setTextDialog(null)}
        />
      )}

      <ToolpathDialog
        visible={toolpathDialog !== null}
        title={toolpathDialog?.title ?? ''}
        pocketProcedureId={toolpathDialog?.procedureId ?? null}
        onClose={() => setToolpathDialog(null)}
      />
    </>
  );
};
